import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * Fired instead of a quantity update when the shopper asks for more than the input's `max`.
 * `cart-items-component` listens for it and shows the disclaimer on the affected line.
 *
 * @typedef {Object} QuantityLimitExceededDetail
 * @property {number} requested - The quantity the shopper asked for.
 * @property {number} max - The highest quantity this line accepts.
 * @property {number} cartLine - 1-based cart line index, `NaN` outside the cart.
 * @property {string} [message] - Localized disclaimer from `data-quantity-limit-message`.
 */
export const QUANTITY_LIMIT_EXCEEDED = 'quantity-selector:limit-exceeded';

/**
 * A custom element that allows the user to select a quantity.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 *
 * @extends {Component<Refs>}
 */
class QuantitySelectorComponent extends Component {
  /**
   * Handles the quantity increase event.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();

    const currentQty = parseInt(this.refs.quantityInput.value) || 1;
    this.#onQuantityChange(event, currentQty + 1);
  }

  /**
   * Handles the quantity decrease event.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();

    const currentQty = parseInt(this.refs.quantityInput.value) || 1;
    this.#onQuantityChange(event, Math.max(0, currentQty - 1));
  }

  /**
   * When our input gets focused, we want to fully select the value.
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput)
      return;

    quantityInput.select();
  }

  /**
   * Handles the quantity set event.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    if (event.target instanceof HTMLInputElement) {
      this.refs.quantityInput.value = event.target.value;
    }
    this.#onQuantityChange(event);
  }

  /**
   * Handles the quantity change event.
   * @param {Event} event - The event.
   * @param {number} [expectedQuantity] - The intended quantity after increase/decrease.
   */
  #onQuantityChange(event, expectedQuantity) {
    const { quantityInput } = this.refs;

    // A free-gift cart line's quantity is locked (the gift is granted as a single unit). Such
    // lines normally render a static pill instead of this component; if one still exists inside
    // a gift row (stale DOM after a section morph), snap back and never dispatch an update.
    if (this.dataset.giftLine === 'true' || this.closest('[data-gift-line="true"]') != null) {
      quantityInput.value = quantityInput.defaultValue || quantityInput.value;
      return;
    }

    // The line's cap: remaining stock on a cart line, or a merchant quantity rule. On the cart page
    // a quantity change is a remove-then-re-add, so asking for more than the shop can fulfil deletes
    // the line and then fails to add it back — the cap has to hold before anything is dispatched.
    const max = this.#maxQuantity();
    const isStepper = expectedQuantity !== undefined;
    const isCartLine = quantityInput.dataset.cartLine != null;
    const requested = expectedQuantity ?? this.#parse(quantityInput.value);
    const current = this.#parse(quantityInput.defaultValue);

    if (max != null && requested != null && requested > max) {
      // The +/- stepper is a bounded control, so it settles at the cap — as does the product page,
      // where the quantity is just a number on a form and clamping costs nothing. A quantity typed
      // into a CART line is different: it triggers the destructive remove-then-re-add, and silently
      // substituting a number the shopper didn't ask for would be its own surprise. Restore what the
      // cart holds and refuse the change.
      const settled = isStepper || !isCartLine ? max : (current ?? max);
      quantityInput.value = String(settled);

      /** @type {QuantityLimitExceededDetail} */
      const detail = {
        requested,
        max,
        cartLine: Number(quantityInput.dataset.cartLine),
        message: quantityInput.dataset.quantityLimitMessage
      };
      this.dispatchEvent(new CustomEvent(QUANTITY_LIMIT_EXCEEDED, { bubbles: true, detail }));

      // Already at the settled quantity — there is nothing for the cart to do.
      if (settled === current) return;
    } else if (isStepper) {
      quantityInput.value = String(expectedQuantity);
    }

    this.#checkQuantityRules();
    const newValue = parseInt(quantityInput.value);

    quantityInput.dispatchEvent(
      new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine))
    );
  }

  /**
   * @param {string} value
   * @returns {number | null} The parsed integer, or `null` when the value isn't a number.
   */
  #parse(value) {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : null;
  }

  /**
   * @returns {number | null} The input's `max`, or `null` when the line is uncapped.
   */
  #maxQuantity() {
    const raw = this.refs.quantityInput.getAttribute('max');
    return raw ? this.#parse(raw) : null;
  }

  /**
   * Checks the quantity rules are met.
   *
   * Compares numerically — `min`/`max`/`value` are strings, so the original comparison sorted them
   * lexicographically and let "10" slip past a max of "9".
   */
  #checkQuantityRules = () => {
    const { quantityInput } = this.refs;
    const value = this.#parse(quantityInput.value);
    if (value == null) return;

    const min = this.#parse(quantityInput.getAttribute('min') ?? '');
    const max = this.#maxQuantity();

    if (min != null && value < min) quantityInput.value = String(min);
    if (max != null && value > max) quantityInput.value = String(max);
  };

  /**
   * Gets the quantity input.
   * @returns {HTMLInputElement} The quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error(
        'Missing <input ref="quantityInput" /> inside <quantity-selector-component />'
      );
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}
